import React, { Component, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

import { TextColor } from '../../../../constants/Theme'
import { OFFLINE_NETWORK_CHECKING_DEFER } from '../../../../services/api'

/**
 * Offline error page, with no dependencies from the Layout HOC.
 *
 * @param {object} props - Component props.
 * @param {Function} props.onOnline - Function to be called when
 * the user gets back online.
 * @returns {Component} The component itself.
 */
export default function OfflineErrorPage({ onOnline }) {
    const checkNetworkStatusIntervalRef = useRef(null)

    const handleCheckNetworkStatus = () => {
        if (navigator.onLine && onOnline) {
            clearInterval(checkNetworkStatusIntervalRef.current)
            checkNetworkStatusIntervalRef.current = null
            onOnline()
        }
    }

    const handleBeforeUnmount = () => {
        if (checkNetworkStatusIntervalRef.current) {
            clearInterval(checkNetworkStatusIntervalRef.current)
        }
    }

    useEffect(() => {
        if (onOnline && window.navigator && 'onLine' in window.navigator) {
            checkNetworkStatusIntervalRef.current = setInterval(
                handleCheckNetworkStatus,
                OFFLINE_NETWORK_CHECKING_DEFER
            )
        }

        return handleBeforeUnmount
    }, [])

    return (
        <div
            data-testid="ErrorBoundary-OfflineErrorPage-index"
            style={{
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden auto',
                position: 'absolute',
                display: 'flex',
                height: 'calc(var(--vh, 1vh) * 100)',
                width: '100%',
                left: 0,
                top: 0
            }}
        >
            <div
                style={{
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    display: 'flex',
                    margin: '16px 20px'
                }}
            >
                <div
                    style={{
                        justifyContent: 'center',
                        flexBasis: '100%',
                        maxWidth: '100%',
                        flexGrow: 0,
                        display: 'flex',
                        margin: '0px 0 40px'
                    }}
                >
                    <svg
                        style={{
                            fontSize: '5rem',
                            height: '1em',
                            width: '1em'
                        }}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 64 64"
                    >
                        <path fill="#FFAB00" d="M10.587454 23.060336H8.820842c-4.416528 0-8.010668 3.59414-8.010668 8.041127v24.032004c0 4.416528 3.59414 8.010668 8.010668 8.010668h1.766612c4.416528 0 8.010668-3.59414 8.010668-8.010668V31.101463c0-4.446987-3.59414-8.041127-8.010668-8.041127z" />
                        <path fill="#2979FF" d="M55.1487.825401H31.055778c-4.446987 0-8.010668 3.59414-8.010668 8.010668v1.766612c0 4.416528 3.59414 8.010668 8.010668 8.010668h24.123381c4.416528 0 8.010668-3.59414 8.010668-8.010668V8.836069C63.159368 4.419541 59.565228.825401 55.1487.825401z" />
                        <path fill="#F80046" d="M10.617913.825401H8.820843c-4.416528 0-8.010668 3.59414-8.010668 8.010668v1.766612c0 4.416528 3.59414 8.010668 8.010668 8.010668h1.766612c4.416528 0 8.010668-3.59414 8.010668-8.010668V8.83607c.030459-4.416528-3.533222-8.010668-7.980209-8.010668z" />
                        <path fill="#00CC3E" d="M55.118241 23.060336H31.055778c-4.446987 0-8.010668 3.59414-8.010668 8.041127v24.062463c0 4.416528 3.59414 8.010668 8.010668 8.010668h24.062463c4.416528 0 8.010668-3.59414 8.010668-8.010668V31.101463c0-4.446987-3.59414-8.041127-8.010668-8.041127z" />
                    </svg>
                </div>
                <h2
                    style={{
                        marginBottom: '.375em',
                        fontFamily: '"ProximaNova", "Helvetica", "Arial", sans-serif',
                        lineHeight: 1.5,
                        fontWeight: 700,
                        textAlign: 'center',
                        fontSize: '1.5rem',
                        color: TextColor.PRIMARY
                    }}
                >
                    It looks like you are offline
                </h2>
                <p
                    style={{
                        letterSpacing: '0.00938em',
                        marginBottom: '.75em',
                        lineHeight: 1.167,
                        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                        fontWeight: 400,
                        flexBasis: '100%',
                        textAlign: 'center',
                        maxWidth: '100%',
                        fontSize: '1rem',
                        flexGrow: 0,
                        color: TextColor.SECONDARY
                    }}
                >
                    Please ensure that you are connected to the Internet and Sheetgo will try to resume.
                </p>
            </div>
        </div>
    )
}

OfflineErrorPage.propTypes = {
    onOnline: PropTypes.func
}

OfflineErrorPage.defaultProps = {
    onOnline: null
}
